<template>
  <div class="desktop-draws-section">
    <div class="desktop-draws-header">
      <h2 class="desktop-section-title">
        {{ $t('detail.selectDraws') }}
      </h2>
    </div>
    
    <div class="desktop-draws-content">
      <div class="desktop-draws-grid">
        <div
          v-for="(draw, index) in props.draws"
          :key="draw.event_id"
          class="desktop-draw-item"
          :class="{ 'selected': props.selectedDraws[index] }"
          @click="toggleDrawSelection(index)"
        >
          <div class="desktop-draw-content">
            <div class="desktop-time-icon">
              <img
                v-if="!isLite"
                src="/img/icons/timer.png"
                :alt="$t('detail.drawTimeAlt')"
              >
              <span
                v-else
                class="timer-icon-lite"
                aria-hidden="true"
              >⏱</span>
            </div>
            <div class="desktop-draw-info">
              <span class="desktop-draw-date">{{ formatDate(new Date(draw.draw_date)) }}</span>
              <span class="desktop-draw-time">{{ formatTime(new Date(draw.draw_date)) }}</span>
            </div>
          </div>
          <label
            class="desktop-checkbox-wrapper"
            @click.stop
          >
            <input 
              type="checkbox" 
              :checked="props.selectedDraws[index]" 
              @change="handleCheckboxChange(index, $event)"
            >
            <span class="desktop-checkmark" />
          </label>
        </div>
      </div>
      
      <!-- Quick Actions -->
      <div class="desktop-quick-actions">
        <button
          type="button"
          class="desktop-action-btn primary"
          :disabled="allSelected"
          @click="selectAll"
        >
          {{ $t('draws.selectAll') }}
        </button>
        <button
          type="button"
          class="desktop-action-btn secondary"
          :disabled="selectedCount <= 1"
          @click="clearAll"
        >
          {{ $t('draws.clearAll') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useIntegrationMode } from '@/composables/useIntegrationMode';

const { isLite } = useIntegrationMode();

interface Draw {
  event_id: string | number;
  draw_date: string;
}

interface Props {
  draws: Draw[];
  selectedDraws: boolean[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update-selected-draws': [index: number, checked: boolean];
  'toggle-draw-selection': [index: number];
}>();

// Computed properties
const selectedCount = computed(() => {
  return props.selectedDraws.filter(Boolean).length;
});

const allSelected = computed(() => {
  return props.selectedDraws.every(Boolean);
});

// Methods
// Ensure at least one draw is always selected
const ensureMinimumSelection = () => {
  const selectedCount = props.selectedDraws.filter(Boolean).length;
  if (selectedCount === 0 && props.draws.length > 0) {
    // Select the first draw if none are selected
    emit('update-selected-draws', 0, true);
  }
};

// Watch for changes in selectedDraws to ensure minimum selection
watch(() => props.selectedDraws, () => {
  ensureMinimumSelection();
}, { immediate: true });

const toggleDrawSelection = (index: number) => {
  emit('toggle-draw-selection', index);
};

const handleCheckboxChange = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update-selected-draws', index, target.checked);
};

const selectAll = () => {
  props.draws.forEach((_, index) => {
    if (!props.selectedDraws[index]) {
      emit('update-selected-draws', index, true);
    }
  });
};

const clearAll = () => {
  // Only clear if there are multiple draws selected
  // Keep at least one draw selected
  const selectedIndices = props.selectedDraws
    .map((selected, index) => selected ? index : -1)
    .filter(index => index !== -1);
  
  if (selectedIndices.length > 1) {
    // Clear all except the first one
    selectedIndices.slice(1).forEach(index => {
      emit('update-selected-draws', index, false);
    });
  }
};

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};
</script>

<script lang="ts">
export default {
  name: "DesktopDrawsSection",
};
</script>

<style scoped>
.desktop-draws-section {
  background: var(--colour-surface-cont-lowest);
  border-radius: var(--detail-panel-radius, 4px);
  margin-bottom: 12px;
  overflow: hidden;
  border: var(--detail-panel-border-width, 1px) solid var(--colour-surface-cont);
}

.desktop-draws-header {
  padding: 12px 16px;
  background: var(--surface-detail-header);
  border-bottom: 1px solid var(--border-detail-panel);
}

.desktop-section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-detail-header);
  margin: 0;
  text-transform: none;
}

.desktop-draws-content {
  padding: 16px;
  background: var(--colour-surface-cont-lowest);
}

.desktop-draws-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.desktop-draw-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--surface-detail-card);
  border: 1px solid var(--border-detail-card);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.desktop-draw-item:hover {
  border-color: var(--colour-orange-button);
  background: var(--surface-detail-card);
}

.desktop-draw-item.selected {
  border-color: var(--colour-orange-button);
  background: var(--surface-detail-card);
}

.desktop-draw-item.selected .desktop-draw-date {
  color: var(--text-detail-card);
}

.desktop-draw-item.selected .desktop-draw-time {
  color: var(--detail-draw-selected-time-color, var(--colour-orange-text-colour));
}

.desktop-draw-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.desktop-time-icon {
  display: none;
}

.desktop-draw-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.desktop-draw-date {
  font-size: 13px;
  color: var(--text-detail-card);
  font-weight: 600;
}

.desktop-draw-time {
  font-size: 12px;
  color: var(--colour-orange-text-colour);
  font-weight: 500;
}

.desktop-checkbox-wrapper {
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.desktop-checkbox-wrapper input {
  opacity: 0;
  width: 0;
  height: 0;
}

.desktop-checkmark {
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  background: var(--colour-surface-cont-lowest);
  border: 1.5px solid var(--colour-surface-cont-high);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.desktop-checkbox-wrapper input:checked ~ .desktop-checkmark {
  border-color: var(--colour-orange-button);
  background: var(--colour-orange-button);
}

.desktop-checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.desktop-checkbox-wrapper input:checked ~ .desktop-checkmark:after {
  display: block;
  content: '✔';
  color: var(--text-button);
  font-size: 14px;
  line-height: 1;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.desktop-quick-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
}

.desktop-action-btn {
  padding: 12px;
  border: 1px solid var(--border-detail-quick-action);
  border-radius: 4px;
  background: var(--surface-detail-quick-action);
  color: var(--text-detail-quick-action);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.desktop-action-btn.primary {
  background: var(--surface-detail-quick-action-primary);
  color: var(--text-detail-quick-action-primary);
  border-color: var(--border-detail-quick-action-primary);
}

.desktop-action-btn.primary:hover:not(:disabled) {
  background: var(--surface-detail-quick-action-primary-hover);
  color: var(--text-detail-quick-action-primary);
  border-color: var(--border-detail-quick-action-primary);
}

.desktop-action-btn.secondary:hover:not(:disabled),
.desktop-action-btn:not(.primary):hover:not(:disabled) {
  background: var(--surface-detail-quick-action-hover);
  color: var(--text-detail-quick-action);
  border-color: var(--border-detail-quick-action);
}

.desktop-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .desktop-draws-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .desktop-draws-grid {
    grid-template-columns: 1fr;
  }
}
</style>
